import React, { useCallback, useState } from 'react';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import Field from '../Field';
import MessageBodyEditor from '../students/MessageBodyEditor';
import useMessageBodyEdits from '../../hooks/useMessageBodyEdits';
import { composeLimits } from '../../domain/notifications';
import { composeWord, messageTemplateFormRefusal, messageTemplateLabelMax } from '../../domain/compose';
import s from './settings.module.css';

// Add Custom Email Template, and its edit (#608). MMS's drawer: Title, Email
// Subject with "Optional" under it, and Message in the composer's editor,
// placeholder menu included; Preview, Cancel and Save at its foot. Ours is
// MessageBodyEditor.jsx, with Preview in its toolbar as the composer has it.
//
// `template` is the one being edited, or null for Add New. Save writes
// through the provider (useMessageTemplates.jsx): a new one as Save New
// Template writes it, an edit as the fields that changed. The core refuses a
// template with no Title or no Message (`template_form_refusal`): a template
// is picked to put its message in. MMS's Save is enabled with a Title alone;
// whether it then refuses an empty Message is unrecorded.
//
// Mounted by its parent only while open, keyed on the template, so each
// opening starts from the template as stored.
export default function CustomTemplateDrawer({ template = null, templates, onClose }) {
  const [label, setLabel] = useState(template?.label || '');
  const [subject, setSubject] = useState(template?.subject || '');
  const [body, setBody] = useState(template?.body || '');
  const [notice, setNotice] = useState(null);
  // `{ field, message }`: a refusal under the box it is about — the Title
  // for a missing name, the Message for the rest and for a write refused.
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const apply = useCallback((patch) => {
    if ('body' in patch) {
      setBody(patch.body);
      setNotice(null);
      setError(null);
    } else {
      setNotice(patch.notice);
    }
  }, []);
  const edits = useMessageBodyEdits(body, apply);

  const save = () => {
    const refusal = messageTemplateFormRefusal(label, body);
    if (refusal) {
      setError({ field: refusal.code === 'template_name_missing' ? 'label' : 'body', message: refusal.message });
      return;
    }
    setSaving(true);
    const write = template
      ? templates.updateTemplate(template, { label, subject, body })
      : templates.saveTemplate({ label, subject, body });
    write.then(
      () => {
        setSaving(false);
        onClose();
      },
      (err) => {
        setSaving(false);
        setError({ field: 'body', message: err?.message || String(err) });
      },
    );
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={composeWord(template ? 'custom_edit_title' : 'custom_add_title')}
      width={560}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>{composeWord('custom_cancel')}</Button>
          <FooterSpacer />
          <Button loading={saving} onClick={save}>{composeWord('custom_save')}</Button>
        </>
      )}
    >
      <div className={s.form}>
        <Field label={composeWord('custom_title')} required error={error?.field === 'label' ? error.message : null}>
          <input
            className={s.input}
            value={label}
            maxLength={messageTemplateLabelMax()}
            onChange={(e) => { setLabel(e.target.value); setError(null); }}
          />
        </Field>
        <Field label={composeWord('custom_subject')} hint={composeWord('custom_optional')}>
          <input
            className={s.input}
            value={subject}
            maxLength={composeLimits().subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </Field>
        <div>
          <MessageBodyEditor
            label={composeWord('custom_message')}
            required
            value={body}
            onChange={(next) => { setBody(next); setNotice(null); setError(null); }}
            edits={edits}
            notice={notice}
            error={error?.field === 'body' ? error.message : null}
          />
        </div>
      </div>
    </Drawer>
  );
}
